import { StandardEvents } from '@shopify/events';

/**
 * Mobile product purchase presentation and selection-safety layer.
 * Horizon remains the owner of variant resolution, inventory, price, cart, and checkout behavior.
 */
class MobileProductPurchase extends HTMLElement {
  connectedCallback() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    this.hidden = false;
    const detailsId = `ProductInformation-${this.dataset.sectionId || ''}`;
    this.details = document.getElementById(detailsId)?.matches('[data-mobile-product-details]')
      ? document.getElementById(detailsId)
      : this.closest('.product-information')?.querySelector('[data-mobile-product-details]');
    if (!this.details) return;

    this.backdrop = this.querySelector('[data-mobile-purchase-backdrop]');
    this.openers = this.querySelectorAll('[data-mobile-purchase-open]');
    this.summaryText = this.querySelector('[data-mobile-purchase-selected-option]');
    this.summaryImage = this.querySelector('[data-mobile-purchase-summary-image]');
    this.closeButton = this.details.querySelector('[data-mobile-purchase-close]');
    this.picker = this.details.querySelector('variant-picker');
    this.form = this.details.querySelector('product-form-component form');
    this.sheetImage = this.details.querySelector('[data-mobile-product-sheet-image]');
    this.sheetImageButton = this.details.querySelector('[data-mobile-product-sheet-image-button]');
    this.sheetPrice = this.details.querySelector('[data-mobile-product-sheet-price]');
    this.sheetVariantName = this.details.querySelector('[data-mobile-product-sheet-variant-name]');
    this.stock = this.details.querySelector('[data-mobile-product-sheet-stock]');
    this.nativePrice = this.details.querySelector('product-price');
    this.variantPresentationMap = this.details.querySelector('[data-mobile-variant-presentation-map]');
    this.variantViewer = this.details.querySelector('[data-mobile-variant-viewer]');
    this.variantViewerTrack = this.details.querySelector('[data-mobile-variant-viewer-track]');
    this.variantViewerClose = this.details.querySelector('[data-mobile-variant-viewer-close]');
    this.purchaseFooter = this.details.querySelector('.buy-buttons-block');
    this.intentionalSelections = new Map();

    this.openers.forEach((opener) => opener.addEventListener('click', this.open));
    this.closeButton?.addEventListener('click', this.close);
    this.backdrop?.addEventListener('click', this.close);
    this.picker?.addEventListener('change', this.captureIntentionalSelection, true);
    this.sheetImageButton?.addEventListener('click', this.openSelectedVariantMedia);
    this.variantViewerClose?.addEventListener('click', this.closeVariantViewer);
    this.variantViewer?.addEventListener('close', this.handleVariantViewerClose);
    this.variantViewerTrack?.addEventListener('scroll', this.handleVariantViewerScroll, { passive: true });
    this.form?.addEventListener('submit', this.preventIncompletePurchase, true);
    this.productEventTarget = this.details.closest('.shopify-section, dialog');
    this.productEventTarget?.addEventListener(StandardEvents.productSelect, this.handleResolvedProductSelect);
    document.addEventListener('keydown', this.handleKeydown);
    this.wrapDescription();
    this.observePicker();
    this.observePurchaseFooter();
    this.updatePresentation();
  }

  disconnectedCallback() {
    this.openers?.forEach((opener) => opener.removeEventListener('click', this.open));
    this.closeButton?.removeEventListener('click', this.close);
    this.backdrop?.removeEventListener('click', this.close);
    this.picker?.removeEventListener('change', this.captureIntentionalSelection, true);
    this.sheetImageButton?.removeEventListener('click', this.openSelectedVariantMedia);
    this.variantViewerClose?.removeEventListener('click', this.closeVariantViewer);
    this.variantViewer?.removeEventListener('close', this.handleVariantViewerClose);
    this.variantViewerTrack?.removeEventListener('scroll', this.handleVariantViewerScroll);
    this.form?.removeEventListener('submit', this.preventIncompletePurchase, true);
    this.productEventTarget?.removeEventListener(StandardEvents.productSelect, this.handleResolvedProductSelect);
    this.pickerObserver?.disconnect();
    this.footerResizeObserver?.disconnect();
    document.removeEventListener('keydown', this.handleKeydown);
    this.unlockPage();
  }

  open = async (event) => {
    event.preventDefault();
    if (this.isOpening || this.details?.hasAttribute('data-mobile-purchase-open')) return;
    this.isOpening = true;
    this.lastTrigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    try {
      if (this.lastTrigger?.classList.contains('mobile-product-purchase__action')) this.resetRequiredSelections();
      await this.closeActiveZoomDialogs();
      this.setAttribute('data-mobile-purchase-open', '');
      this.details.setAttribute('data-mobile-purchase-open', '');
      this.details.setAttribute('role', 'dialog');
      this.details.setAttribute('aria-modal', 'true');
      this.details.setAttribute('aria-labelledby', 'mobile-product-sheet-title');
      const sheetHeader = this.details.querySelector('[data-mobile-product-sheet-header]');
      if (sheetHeader) sheetHeader.hidden = false;
      this.details.querySelector('.mobile-product-sheet__title')?.setAttribute('id', 'mobile-product-sheet-title');
      if (this.backdrop) this.backdrop.hidden = false;
      this.lockPage();
      this.closeButton?.focus({ preventScroll: true });
      this.updatePurchaseGate();
      window.requestAnimationFrame(this.updateFooterReserve);
    } finally {
      this.isOpening = false;
    }
  };

  async closeActiveZoomDialogs() {
    const dialogs = Array.from(document.querySelectorAll('zoom-dialog dialog[open]'));
    await Promise.all(
      dialogs.map((dialog) => {
        const zoomDialog = dialog.closest('zoom-dialog');
        if (typeof zoomDialog?.close === 'function') return zoomDialog.close();
        dialog.close();
        return Promise.resolve();
      })
    );
  }

  close = () => {
    if (!this.details?.hasAttribute('data-mobile-purchase-open')) return;
    this.details.removeAttribute('data-mobile-purchase-open');
    this.removeAttribute('data-mobile-purchase-open');
    this.details.removeAttribute('role');
    this.details.removeAttribute('aria-modal');
    this.details.removeAttribute('aria-labelledby');
    if (this.backdrop) this.backdrop.hidden = true;
    this.unlockPage();
    this.lastTrigger?.focus({ preventScroll: true });
  };

  handleKeydown = (event) => {
    if (!this.details?.hasAttribute('data-mobile-purchase-open')) return;
    if (this.variantViewer?.open && event.key === 'Escape') {
      event.preventDefault();
      this.variantViewer.close();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  trapFocus(event) {
    const focusable = Array.from(this.details.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(
      (element) => element instanceof HTMLElement && element.getClientRects().length > 0
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  captureIntentionalSelection = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.fieldsetIndex) return;
    this.intentionalSelections.set(input.dataset.fieldsetIndex, input.dataset.optionValueId || '');
    this.clearUnintentionalSelections();
    window.requestAnimationFrame(() => this.updatePurchaseGate());
  };

  resetRequiredSelections() {
    this.intentionalSelections.clear();
    this.clearUnintentionalSelections();
    this.updatePurchaseGate();
  }

  clearUnintentionalSelections() {
    this.picker?.querySelectorAll('[data-fieldset-index]').forEach((fieldset) => {
      const index = fieldset.getAttribute('data-fieldset-index');
      if (index && this.intentionalSelections.has(index)) return;
      fieldset.querySelectorAll('input').forEach((input) => {
        input.checked = false;
        if (input.dataset.currentChecked !== 'false') input.dataset.currentChecked = 'false';
        if (input.dataset.previousChecked !== 'false') input.dataset.previousChecked = 'false';
      });
    });
  }

  get optionGroupCount() {
    return this.picker?.querySelectorAll('[data-fieldset-index]').length || 0;
  }

  get selectionComplete() {
    if (!this.optionGroupCount || this.intentionalSelections.size !== this.optionGroupCount) return false;
    return Array.from(this.picker?.querySelectorAll('[data-fieldset-index]') || []).every((fieldset) => fieldset.querySelector('input:checked'));
  }

  updatePurchaseGate() {
    const complete = this.selectionComplete;
    this.details?.toggleAttribute('data-mobile-selection-complete', complete);
    const addToCart = this.details?.querySelector('.add-to-cart-button');
    const accelerated = this.details?.querySelector('.accelerated-checkout-block');
    if (addToCart instanceof HTMLButtonElement) {
      if (!complete) {
        if (!addToCart.dataset.mobileSelectionGuard) addToCart.dataset.mobileSelectionGuard = String(addToCart.disabled);
        addToCart.disabled = true;
      } else if (addToCart.dataset.mobileSelectionGuard) {
        // Let Horizon's product-form event restore availability after it resolves the real combination.
        delete addToCart.dataset.mobileSelectionGuard;
      }
    }
    if (accelerated instanceof HTMLElement) {
      if (!complete) {
        accelerated.dataset.mobileSelectionGuard = 'true';
        accelerated.hidden = true;
      } else if (accelerated.dataset.mobileSelectionGuard) {
        delete accelerated.dataset.mobileSelectionGuard;
      }
    }
  }

  preventIncompletePurchase = (event) => {
    if (this.selectionComplete) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.details.querySelector('[data-fieldset-index] input')?.focus();
  };

  observePicker() {
    if (!this.picker) return;
    this.pickerObserver = new MutationObserver(() => {
      if (!this.details.hasAttribute('data-mobile-purchase-open')) return;
      window.requestAnimationFrame(() => {
        this.observePurchaseFooter();
        if (!this.selectionComplete) this.clearUnintentionalSelections();
        this.updatePurchaseGate();
      });
    });
    this.pickerObserver.observe(this.picker, { childList: true, subtree: true });
  }

  observePurchaseFooter() {
    const footer = this.details?.querySelector('.buy-buttons-block');
    if (!(footer instanceof HTMLElement)) return;
    if (footer === this.observedPurchaseFooter) return;
    if (this.observedPurchaseFooter instanceof HTMLElement) this.footerResizeObserver?.unobserve(this.observedPurchaseFooter);
    this.purchaseFooter = footer;
    this.observedPurchaseFooter = footer;
    this.footerResizeObserver?.observe(footer);
    this.updateFooterReserve();
  }

  footerResizeObserver = new ResizeObserver(() => {
    window.requestAnimationFrame(this.updateFooterReserve);
  });

  updateFooterReserve = () => {
    if (!this.details?.hasAttribute('data-mobile-purchase-open') || !(this.purchaseFooter instanceof HTMLElement)) return;
    const footerHeight = Math.ceil(this.purchaseFooter.getBoundingClientRect().height);
    if (footerHeight > 0) this.details.style.setProperty('--mobile-purchase-footer-height', `${footerHeight}px`);
  };

  handleResolvedProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target !== this.picker) return;
    const requestId = (this.productSelectRequestId || 0) + 1;
    this.productSelectRequestId = requestId;

    event.promise
      .then(({ detail }) => {
        if (requestId !== this.productSelectRequestId || !detail?.resource?.id) return;
        window.requestAnimationFrame(() => this.syncResolvedVariant(detail.resource, requestId));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mobile-product-purchase] Variant resolution failed', error);
      });
  };

  syncResolvedVariant(resource, requestId) {
    if (requestId !== this.productSelectRequestId) return;
    const variantId = this.details.querySelector('product-form-component input[name="id"]')?.value;
    if (!variantId || variantId !== String(resource.id)) return;
    const presentation = this.variantPresentationMap?.querySelector(`[data-variant-id="${CSS.escape(variantId)}"]`);
    if (!presentation) return;
    this.updatePresentation(presentation);
  }

  updatePresentation = (presentation = null) => {
    const nativeAddToCart = this.details.querySelector('.add-to-cart-button');
    this.nativePrice = this.details.querySelector('product-price');

    if (presentation) {
      const variantId = presentation.dataset.variantId || '';
      const variantTitle = presentation.dataset.variantTitle || '';
      if (this.summaryText) this.summaryText.textContent = variantTitle;
      if (this.sheetVariantName) this.sheetVariantName.textContent = variantTitle;
      if (this.stock) this.stock.textContent = presentation.dataset.variantAvailable === 'true' ? 'In stock' : 'Sold out';
      const nativePriceContainer = this.nativePrice?.querySelector('[ref="priceContainer"]');
      if (this.sheetPrice && nativePriceContainer) this.sheetPrice.innerHTML = nativePriceContainer.innerHTML;
      this.updateSheetImage(presentation);
    } else if (this.stock && this.optionGroupCount > 0) {
      this.stock.textContent = 'Select options';
    } else if (this.stock && nativeAddToCart instanceof HTMLButtonElement) {
      this.stock.textContent = nativeAddToCart.disabled ? 'Sold out' : 'In stock';
    }
  };

  updateSheetImage(presentation) {
    const imageUrl = presentation.dataset.variantMediaUrl;
    const mediaId = presentation.dataset.variantMediaId;
    if (!imageUrl || !mediaId) {
      this.sheetImageButton?.setAttribute('hidden', '');
      return;
    }
    const image = this.sheetImage || document.createElement('img');
    image.className = 'mobile-product-sheet__image';
    image.setAttribute('data-mobile-product-sheet-image', '');
    image.dataset.mobileProductSheetMediaId = mediaId;
    image.src = imageUrl;
    image.alt = presentation.dataset.variantTitle || '';
    image.width = 96;
    image.height = 96;
    image.loading = 'eager';
    if (!this.sheetImage) this.sheetImageButton?.append(image);
    this.sheetImage = image;
    this.sheetImageButton?.removeAttribute('hidden');
  }

  openSelectedVariantMedia = (event) => {
    const variantId = this.details.querySelector('product-form-component input[name="id"]')?.value;
    if (variantId && this.openVariantViewer(variantId)) return;

    // A variant without its own unique media stays selectable in A Pages and uses the existing gallery viewer only.
    const mediaId = this.sheetImage?.dataset.mobileProductSheetMediaId;
    const gallery = this.closest('.product-information')?.querySelector('media-gallery');
    if (!mediaId || !gallery || typeof gallery.zoom !== 'function') return;
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const index = slides.findIndex((slide) => slide.querySelector(`.product-media[data-media-id="${CSS.escape(mediaId)}"]`));
    if (index >= 0) gallery.zoom(index, event);
  };

  getVariantViewerItems() {
    return Array.from(this.variantViewerTrack?.querySelectorAll('[data-mobile-variant-viewer-item]') || []);
  }

  getVariantOptions(item) {
    try {
      const options = JSON.parse(item.dataset.variantOptions || '[]');
      return Array.isArray(options) ? options : [];
    } catch {
      return [];
    }
  }

  openVariantViewer(variantId) {
    if (!(this.variantViewer instanceof HTMLDialogElement) || !this.variantViewerTrack) return false;
    const items = this.getVariantViewerItems();
    const selectedItem = items.find((item) => item.dataset.variantId === String(variantId));
    if (!selectedItem) return false;

    const selectedOptions = this.getVariantOptions(selectedItem);
    const candidateGroups = selectedOptions
      .map((_, index) => index)
      .filter((index) => items.filter((item) => {
        const options = this.getVariantOptions(item);
        return options.length === selectedOptions.length && options.every((value, optionIndex) => optionIndex === index || value === selectedOptions[optionIndex]);
      }).length > 1);
    const colorGroup = candidateGroups.find((index) => this.picker?.querySelector(`[data-fieldset-index="${index}"] legend`)?.textContent?.trim().toLowerCase().includes('color'));
    const browseGroup = colorGroup ?? candidateGroups[0];
    const sequence = browseGroup === undefined
      ? [selectedItem]
      : items.filter((item) => {
        const options = this.getVariantOptions(item);
        return options.length === selectedOptions.length && options.every((value, optionIndex) => optionIndex === browseGroup || value === selectedOptions[optionIndex]);
      });

    this.variantViewerSequence = sequence;
    items.forEach((item) => item.toggleAttribute('hidden', !sequence.includes(item)));
    this.viewedVariantId = String(variantId);
    this.variantViewer.showModal();
    window.requestAnimationFrame(() => {
      const index = sequence.indexOf(selectedItem);
      this.variantViewerTrack.scrollLeft = index * this.variantViewerTrack.clientWidth;
    });
    return true;
  }

  handleVariantViewerScroll = () => {
    if (!this.variantViewer?.open || !this.variantViewerSequence?.length || this.variantViewerScrollFrame) return;
    this.variantViewerScrollFrame = window.requestAnimationFrame(() => {
      this.variantViewerScrollFrame = undefined;
      const index = Math.round(this.variantViewerTrack.scrollLeft / this.variantViewerTrack.clientWidth);
      const item = this.variantViewerSequence[index];
      if (item) this.viewedVariantId = item.dataset.variantId || this.viewedVariantId;
    });
  };

  closeVariantViewer = () => {
    this.variantViewer?.close();
  };

  handleVariantViewerClose = () => {
    const viewedItem = this.variantViewerSequence?.find((item) => item.dataset.variantId === this.viewedVariantId);
    this.variantViewerSequence = undefined;
    if (viewedItem) this.commitVariantViewerSelection(viewedItem);
  };

  commitVariantViewerSelection(item) {
    const options = this.getVariantOptions(item);
    const nativeInputs = options.map((value, index) => this.findNativeOption(index, value));
    if (!nativeInputs.length || nativeInputs.some((input) => !(input instanceof HTMLInputElement))) return;
    const currentVariantId = this.details.querySelector('product-form-component input[name="id"]')?.value;
    if (currentVariantId === item.dataset.variantId) return;

    this.intentionalSelections.clear();
    nativeInputs.forEach((input) => {
      this.intentionalSelections.set(input.dataset.fieldsetIndex || '', input.dataset.optionValueId || '');
      this.picker.updateSelectedOption(input);
    });
    this.picker.variantChanged({ target: nativeInputs[nativeInputs.length - 1] });
    window.requestAnimationFrame(() => this.updatePurchaseGate());
  }

  findNativeOption(index, value) {
    const fieldset = this.picker?.querySelector(`[data-fieldset-index="${index}"]`);
    return Array.from(fieldset?.querySelectorAll('label') || []).find((label) => label.querySelector('[data-key="variant-option-text"]')?.textContent?.trim() === value)?.querySelector('input') || null;
  }

  updateImage(target, source, selector) {
    const container = target?.parentElement || (selector.includes('summary') ? this.querySelector('.mobile-product-purchase__summary') : this.details.querySelector('[data-mobile-product-sheet-header]'));
    if (!container) return;
    const image = target || document.createElement('img');
    image.setAttribute(selector.slice(1, -1), '');
    image.src = source.currentSrc || source.src;
    image.alt = '';
    image.width = 40;
    image.height = 40;
    image.loading = 'lazy';
    image.className = selector.includes('summary') ? '' : 'mobile-product-sheet__image';
    if (!target) container.prepend(image);
    if (selector.includes('summary')) this.summaryImage = image;
    else this.sheetImage = image;
  }

  wrapDescription() {
    const productInformation = this.closest('.product-information');
    const description = Array.from(productInformation?.children || []).find((element) => element.matches?.('rte-formatter.text-block.rte'));
    if (!(description instanceof HTMLElement) || description.parentElement?.matches('[data-mobile-product-description]')) return;
    const details = document.createElement('details');
    details.dataset.mobileProductDescription = '';
    const summary = document.createElement('summary');
    summary.textContent = 'Product Details';
    description.parentElement?.insertBefore(details, description);
    details.append(summary, description);
  }

  lockPage() {
    if (this.pageLocked) return;
    this.previousDocumentOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.pageLocked = true;
  }

  unlockPage() {
    if (this.pageLocked && this.previousDocumentOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousDocumentOverflow;
      this.previousDocumentOverflow = undefined;
    }
    this.pageLocked = false;
  }
}

if (!customElements.get('mobile-product-purchase')) customElements.define('mobile-product-purchase', MobileProductPurchase);
